import {
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tabs,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import Student from '../../../model/Student';

type VerificationReportItem = { status: 'success' | 'error'; message: string };

type HeaderMapping = Record<number, string>;

type FieldOption = { key: string; label: string; category: 'identity' | 'result' | 'meta' };

type SimpleValue = string | number | boolean | null | undefined;

type ParsedRow = {
	index?: string;
	firstName?: string;
	lastName?: string;
	identifier?: string;
	school?: string;
	city?: string;
	schoolClass?: string;
	district?: string;
	opp?: string;
	personalTitle?: string;
	flags?: Record<string, boolean>;
	rawLine?: string;
	[key: string]: SimpleValue | Record<string, boolean> | undefined;
};

type VerificationRow = {
	row: ParsedRow;
	status: 'matched' | 'extra' | 'ambiguous';
	diffs: Record<string, { isDiff: boolean; systemValue: string | number | boolean | null }>;
	student: Student | null;
};

type Props = {
	open: boolean;
	onClose: () => void;
	stageKey: string;
	title: string;
	students: Student[];
	getSystemValue?: (student: Student, fieldKey: string) => string | number | boolean | null;
};

const STORAGE_PREFIX = 'protocolMapping:';

const fieldOptions: FieldOption[] = [
	// Meta
	{ key: 'ignore', label: 'Ignoruj', category: 'meta' },

	// Identity fields
	{ key: 'index', label: 'Lp.', category: 'identity' },
	{ key: 'identifier', label: 'Identyfikator (OOOO-NNNNNN)', category: 'identity' },
	{ key: 'firstName', label: 'Imię', category: 'identity' },
	{ key: 'lastName', label: 'Nazwisko', category: 'identity' },
	{ key: 'school', label: 'Szkoła', category: 'identity' },
	{ key: 'city', label: 'Miejscowość', category: 'identity' },
	{ key: 'schoolClass', label: 'Klasa', category: 'identity' },
	{ key: 'district', label: 'Okręg', category: 'identity' },
	{ key: 'opp', label: 'OPP tak/nie', category: 'identity' },
	{ key: 'personalTitle', label: 'Tytuł osobowy (Pan/Pani)', category: 'identity' },

	// Result fields - Stage I
	{ key: 'firstStageStatus', label: 'Etap I - Status', category: 'result' },
	{ key: 'firstStagePoints', label: 'Etap I - Punkty', category: 'result' },
	{ key: 'firstStageQualified', label: 'Etap I - Kwalifikacja', category: 'result' },

	// Result fields - Stage IIA
	{ key: 'secondAStageTopic', label: 'Etap IIA - Temat', category: 'result' },
	{ key: 'secondAOneStagePoints', label: 'Etap IIA - Punkty I (rozprawka)', category: 'result' },
	{ key: 'secondATwoStagePoints', label: 'Etap IIA - Punkty II (test)', category: 'result' },
	{ key: 'secondAStageSum', label: 'Etap IIA - Suma', category: 'result' },
	{ key: 'secondAStageQualified', label: 'Etap IIA - Kwalifikacja', category: 'result' },

	// Result fields - Stage IIB (full olympiad has I, II, III; small olympiad has only one field stored in III)
	{ key: 'secondBOneStagePoints', label: 'Etap IIB - Część I (rozprawka) [duża]', category: 'result' },
	{ key: 'secondBTwoStagePoints', label: 'Etap IIB - Część II (test) [duża]', category: 'result' },
	{ key: 'secondBThreeStagePoints', label: 'Etap IIB - Cześć (część ustna) / Mała OLiJP', category: 'result' },
	{ key: 'secondBStageSum', label: 'Etap IIB - Suma [duża]', category: 'result' },
	{ key: 'secondStageTotalPoints', label: 'Etap II - Suma łączna', category: 'result' },
	{ key: 'secondBStageQualified', label: 'Etap IIB - Kwalifikacja', category: 'result' },

	// Result fields - Stage IIIA
	{ key: 'thirdAStageTopic', label: 'Etap IIIA - Temat', category: 'result' },
	{ key: 'thirdAStagePoints', label: 'Etap IIIA - Punkty', category: 'result' },
	{ key: 'thirdAStageQualified', label: 'Etap IIIA - Kwalifikacja', category: 'result' },

	// Result fields - Stage IIIB
	{ key: 'thirdBOneStagePoints', label: 'Etap IIIB - Punkty I', category: 'result' },
	{ key: 'thirdBTwoStagePoints', label: 'Etap IIIB - Punkty II', category: 'result' },
	{ key: 'thirdBStageTotalPoints', label: 'Etap IIIB - Suma', category: 'result' },
	{ key: 'thirdStageTotalPoints', label: 'Etap III - Suma łączna', category: 'result' },

	// Final
	{ key: 'finalTitle', label: 'Tytuł końcowy', category: 'result' },

	// Additional meta fields from protocols
	{ key: 'topic', label: 'Temat pracy (ogólny)', category: 'meta' },
	{ key: 'testType', label: 'Test A/B', category: 'meta' },
	{ key: 'decision', label: 'Decyzja Komisji', category: 'meta' },
];

const identityFieldKeys = fieldOptions.filter((f) => f.category === 'identity').map((f) => f.key);

const diffColor = 'rgba(244, 67, 54, 0.18)';
const starColor = 'rgba(255, 152, 0, 0.2)';

const normalizeName = (value: string) => value.toLowerCase().replace(/[.,]/g, '').replace(/\s+/g, ' ').trim();
const normalizeText = (value?: string | number | boolean | null) =>
	(value === undefined || value === null ? '' : String(value)).toLowerCase().replace(/\s+/g, ' ').trim();

const parsePointsValue = (raw?: string | null) => {
	if (!raw) {
		return { value: null, hasStar: false };
	}
	const cleaned = raw.replace(/\s+/g, '');
	const hasStar = cleaned.includes('*');
	const numeric = cleaned.replace('*', '').replace(',', '.');
	const parsed = Number(numeric.replace(/[^\d.]/g, ''));
	return {
		value: Number.isNaN(parsed) ? null : parsed,
		hasStar,
	};
};

const autoMapHeader = (header: string) => {
	const normalized = normalizeText(header).replace(/[():.]/g, '');
	if (normalized.startsWith('lp')) return 'index';
	if (normalized.includes('identyfikator')) return 'identifier';
	if (normalized.includes('imi')) return 'firstName';
	if (normalized.includes('nazw')) return 'lastName';
	if (normalized.includes('szko')) return 'school';
	if (normalized.includes('miejsc')) return 'city';
	if (normalized.includes('klas')) return 'schoolClass';
	if (normalized.includes('okr')) return 'district';
	if (normalized.includes('opp')) return 'opp';
	if (normalized.includes('tytuł osobo') || normalized.includes('pan/pani')) return 'personalTitle';

	// Points mapping - more specific first
	if (normalized.includes('rozpraw') && normalized.includes('punkt')) return 'secondBOneStagePoints';
	if (normalized.includes('test') && normalized.includes('punkt') && normalized.includes('język'))
		return 'secondBTwoStagePoints';
	if (normalized.includes('część ust') && normalized.includes('punkt')) return 'secondBThreeStagePoints';
	if (normalized.includes('punktacja za rozpraw')) return 'secondBOneStagePoints';
	if (normalized.includes('punktacja za test')) return 'secondBTwoStagePoints';
	if (normalized.includes('punktacja za część ust')) return 'secondBThreeStagePoints';
	if (normalized.includes('liczba punktów') && normalized.includes('rozpraw')) return 'secondBOneStagePoints';
	if (normalized.includes('liczba punktów') && normalized.includes('test')) return 'secondBTwoStagePoints';
	if (normalized.includes('liczba punktów') && normalized.includes('część ust')) return 'secondBThreeStagePoints';
	if (normalized.includes('suma') && normalized.includes('punkt')) return 'secondBStageSum';

	if (normalized.includes('temat')) return 'topic';
	if (normalized.includes('test') && normalized.includes('a/b')) return 'testType';
	if (normalized.includes('decyzja komisji') || normalized.includes('rekomendacj')) return 'decision';
	if (normalized.includes('kwalifikacj')) return 'secondBStageQualified';
	return '';
};

const parseHeaderRows = (text: string) => {
	const lines = text.replace(/\r\n/g, '\n').split('\n');
	const headerIndex = lines.findIndex((line) => {
		const normalized = normalizeText(line);
		return normalized.includes('lp') && (normalized.includes('imi') || normalized.includes('identyfikator'));
	});
	if (headerIndex === -1) {
		return { headers: [], dataStartIndex: -1 };
	}
	const headerRows: string[] = [];
	let dataStartIndex = headerIndex + 1;
	for (let i = headerIndex; i < lines.length; i += 1) {
		const line = lines[i];
		const firstCell = line.split('\t')[0]?.trim() ?? '';
		if (i !== headerIndex && /^\d+/.test(firstCell)) {
			dataStartIndex = i;
			break;
		}
		if (!line.trim()) {
			continue;
		}
		headerRows.push(line);
	}
	const headers: string[] = [];
	headerRows.forEach((headerRow) => {
		const parts = headerRow.split('\t');
		parts.forEach((part, index) => {
			const trimmed = part.trim();
			if (!trimmed) {
				return;
			}
			headers[index] = headers[index] ? `${headers[index]} ${trimmed}` : trimmed;
		});
	});
	return { headers, dataStartIndex };
};

type StoredMapping = {
	version: number;
	mapping: HeaderMapping;
	identityFields: string[];
};

const loadMapping = (stageKey: string): StoredMapping => {
	try {
		const stored = localStorage.getItem(`${STORAGE_PREFIX}${stageKey}`);
		if (!stored) {
			return { version: 1, mapping: {}, identityFields: ['firstName', 'lastName'] };
		}
		const parsed = JSON.parse(stored);
		if (parsed && typeof parsed === 'object') {
			return {
				version: parsed.version || 1,
				mapping: parsed.mapping || {},
				identityFields: parsed.identityFields || ['firstName', 'lastName'],
			};
		}
	} catch (error) {
		// ignore storage errors
	}
	return { version: 1, mapping: {}, identityFields: ['firstName', 'lastName'] };
};

const saveMapping = (stageKey: string, mapping: HeaderMapping, identityFields: string[]) => {
	try {
		localStorage.setItem(`${STORAGE_PREFIX}${stageKey}`, JSON.stringify({ version: 1, mapping, identityFields }));
	} catch (error) {
		// ignore storage errors
	}
};

const getStudentDisplayClass = (student: Student) =>
	student.schoolClassText && student.schoolClassText.trim() !== ''
		? student.schoolClassText
		: String(student.schoolClass);

const defaultGetSystemValue = (student: Student, fieldKey: string): string | number | boolean | null => {
	switch (fieldKey) {
		case 'identifier':
			return student.district.id.toString().padStart(4, '0') + '-' + student.id.toString().padStart(6, '0');
		case 'firstName':
			return student.firstName;
		case 'lastName':
			return student.lastName;
		case 'school':
			return student.school?.name ?? null;
		case 'city':
			return student.school?.city ?? null;
		case 'schoolClass':
			return getStudentDisplayClass(student);
		case 'district':
			return student.district?.name ?? null;
		case 'personalTitle':
			return student.personalTitle ?? null;

		// Stage I
		case 'firstStageStatus':
			return student.result.firstStageStatus;
		case 'firstStagePoints':
			return student.result.firstStagePoints;
		case 'firstStageQualified':
			return student.result.firstStageQualified;

		// Stage IIA
		case 'secondAStageTopic':
			return student.result.secondAStageTopic;
		case 'secondAOneStagePoints':
			return student.result.secondAOneStagePoints;
		case 'secondATwoStagePoints':
			return student.result.secondATwoStagePoints;
		case 'secondAStageSum':
			return student.result.secondAOneStagePoints + student.result.secondATwoStagePoints;
		case 'secondAStageQualified':
			return student.result.secondAStageQualified;

		// Stage IIB
		case 'secondBOneStagePoints':
			return student.result.secondBOneStagePoints;
		case 'secondBTwoStagePoints':
			return student.result.secondBTwoStagePoints;
		case 'secondBThreeStagePoints':
			return student.result.secondBThreeStagePoints;
		case 'secondBStageSum':
			return (
				student.result.secondBOneStagePoints +
				student.result.secondBTwoStagePoints +
				student.result.secondBThreeStagePoints
			);
		case 'secondStageTotalPoints':
			return student.result.secondStageTotalPoints;
		case 'secondBStageQualified':
			return student.result.secondBStageQualified;

		// Stage IIIA
		case 'thirdAStageTopic':
			return student.result.thirdAStageTopic;
		case 'thirdAStagePoints':
			return student.result.thirdAStagePoints;
		case 'thirdAStageQualified':
			return student.result.thirdAStageQualified;

		// Stage IIIB
		case 'thirdBOneStagePoints':
			return student.result.thirdBOneStagePoints;
		case 'thirdBTwoStagePoints':
			return student.result.thirdBTwoStagePoints;
		case 'thirdBStageTotalPoints':
			return student.result.thirdBStageTotalPoints;
		case 'thirdStageTotalPoints':
			return student.result.thirdAStagePoints + student.result.thirdBStageTotalPoints;

		// Final
		case 'finalTitle':
			return student.result.finalTitle;

		default:
			return null;
	}
};

const ProtocolVerificationDialog = ({ open, onClose, stageKey, title, students, getSystemValue }: Props) => {
	const [verificationText, setVerificationText] = useState('');
	const [verificationReport, setVerificationReport] = useState<VerificationReportItem[]>([]);
	const [verificationRows, setVerificationRows] = useState<VerificationRow[]>([]);
	const [verificationLoading, setVerificationLoading] = useState(false);
	const [headerMapping, setHeaderMapping] = useState<HeaderMapping>({});
	const [identityFields, setIdentityFields] = useState<string[]>(['firstName', 'lastName']);
	const [headers, setHeaders] = useState<string[]>([]);
	const [activeTab, setActiveTab] = useState(0);

	const getValue = getSystemValue || defaultGetSystemValue;

	useEffect(() => {
		if (open) {
			const stored = loadMapping(stageKey);
			setHeaderMapping(stored.mapping);
			setIdentityFields(stored.identityFields);
		}
	}, [open, stageKey]);

	useEffect(() => {
		if (!verificationText.trim()) {
			setHeaders([]);
			return;
		}
		const parsed = parseHeaderRows(verificationText);
		setHeaders(parsed.headers);
		if (!Object.keys(headerMapping).length && parsed.headers.length) {
			const auto: HeaderMapping = {};
			parsed.headers.forEach((header, index) => {
				const mapped = autoMapHeader(header);
				if (mapped) {
					auto[index] = mapped;
				}
			});
			setHeaderMapping(auto);
		}
	}, [verificationText]);

	const mappingOptions = useMemo(() => fieldOptions, []);

	const mappedFieldKeys = useMemo(() => {
		const keys = new Set<string>();
		Object.values(headerMapping).forEach((key) => {
			if (key && key !== 'ignore' && key !== 'index') {
				keys.add(key);
			}
		});
		return Array.from(keys);
	}, [headerMapping]);

	const parseVerificationRows = (text: string, mapping: HeaderMapping) => {
		const lines = text.replace(/\r\n/g, '\n').split('\n');
		const errors: string[] = [];
		const { headers: parsedHeaders, dataStartIndex } = parseHeaderRows(text);
		if (!parsedHeaders.length || dataStartIndex === -1) {
			return { rows: [], errors: ['Nie znaleziono nagłówka tabeli (Lp., Imię lub Identyfikator).'] };
		}
		const rows: ParsedRow[] = [];
		for (let i = dataStartIndex; i < lines.length; i += 1) {
			const line = lines[i];
			if (!line.trim()) {
				continue;
			}
			const parts = line.split('\t');
			const firstCell = parts[0]?.trim() ?? '';
			if (!/^\d+/.test(firstCell)) {
				continue;
			}
			const row: ParsedRow = {
				index: firstCell.replace(/[^\d]/g, ''),
				flags: {},
				rawLine: line,
			};
			parts.forEach((cell, index) => {
				const mapKey = mapping[index];
				if (!mapKey || mapKey === 'ignore') {
					return;
				}
				const value = cell.trim();
				// Check if this is a points field
				const isPointsField =
					mapKey.includes('Points') || mapKey.includes('Sum') || mapKey === 'secondStageTotalPoints';
				if (isPointsField) {
					const parsed = parsePointsValue(value);
					row[mapKey] = parsed.value;
					if (parsed.hasStar && row.flags) {
						row.flags[mapKey] = true;
					}
					return;
				}
				// Check if this is a boolean/qualified field
				if (mapKey.includes('Qualified')) {
					const normalized = normalizeText(value);
					row[mapKey] = normalized === 'tak' || normalized === 'yes' || normalized === '1' || normalized === 'true';
					return;
				}
				row[mapKey] = value;
			});
			// Check if we have identity fields
			const hasIdentity =
				identityFields.length === 0 ||
				identityFields.some((field) => row[field] !== undefined && row[field] !== null && row[field] !== '');
			if (!hasIdentity) {
				errors.push(`Nie rozpoznano pól identyfikacyjnych w wierszu: "${line}"`);
				continue;
			}
			rows.push(row);
		}
		if (!rows.length) {
			errors.push('Nie znaleziono żadnych wierszy z danymi (sprawdź tabulatory).');
		}
		return { rows, errors };
	};

	const matchStudent = (row: ParsedRow, candidates: Student[]): Student[] => {
		if (identityFields.includes('identifier') && row.identifier) {
			const idStr = String(row.identifier).replace(/\s/g, '');
			return candidates.filter((s) => {
				const sysId = s.district.id.toString().padStart(4, '0') + '-' + s.id.toString().padStart(6, '0');
				return sysId === idStr;
			});
		}

		let matches = candidates;

		if (identityFields.includes('firstName') && row.firstName) {
			const norm = normalizeName(String(row.firstName));
			matches = matches.filter((s) => normalizeName(s.firstName) === norm);
		}
		if (identityFields.includes('lastName') && row.lastName) {
			const norm = normalizeName(String(row.lastName));
			matches = matches.filter((s) => normalizeName(s.lastName) === norm);
		}
		if (identityFields.includes('city') && row.city) {
			const norm = normalizeText(row.city as SimpleValue);
			matches = matches.filter((s) => normalizeText(s.school?.city) === norm);
		}
		if (identityFields.includes('schoolClass') && row.schoolClass) {
			const norm = normalizeText(row.schoolClass as SimpleValue);
			matches = matches.filter((s) => normalizeText(getStudentDisplayClass(s)) === norm);
		}
		if (identityFields.includes('school') && row.school) {
			const norm = normalizeText(row.school as SimpleValue);
			matches = matches.filter(
				(s) => normalizeText(s.school?.name)?.includes(norm) || norm.includes(normalizeText(s.school?.name)),
			);
		}
		if (identityFields.includes('district') && row.district) {
			const norm = normalizeText(row.district as SimpleValue);
			matches = matches.filter((s) => normalizeText(s.district?.name)?.includes(norm));
		}

		return matches;
	};

	const handleVerify = () => {
		if (!verificationText.trim()) {
			setVerificationReport([{ status: 'error', message: 'Brak wklejonego tekstu.' }]);
			return;
		}
		setVerificationLoading(true);
		const report: VerificationReportItem[] = [];
		const { rows, errors } = parseVerificationRows(verificationText, headerMapping);
		errors.forEach((message) => report.push({ status: 'error', message }));

		const usedStudentIds = new Set<number>();
		const verifiedRows: VerificationRow[] = [];
		let matchedCount = 0;
		let extraCount = 0;
		let ambiguousCount = 0;
		let diffCount = 0;

		rows.forEach((row) => {
			const matches = matchStudent(row, students);

			if (!matches.length) {
				extraCount += 1;
				verifiedRows.push({ row, status: 'extra', diffs: {}, student: null });
				return;
			}
			if (matches.length > 1) {
				ambiguousCount += 1;
				verifiedRows.push({ row, status: 'ambiguous', diffs: {}, student: null });
				return;
			}
			const student = matches[0];
			usedStudentIds.add(student.id);
			matchedCount += 1;

			const diffs: VerificationRow['diffs'] = {};
			let hasDiff = false;

			// Compare all mapped fields (except identity and meta)
			mappedFieldKeys.forEach((fieldKey) => {
				if (fieldKey === 'index' || identityFields.includes(fieldKey)) {
					return;
				}
				const rowValue = row[fieldKey];
				if (rowValue === undefined || rowValue === null) {
					return;
				}
				const systemValue = getValue(student, fieldKey);
				const isPointsField = fieldKey.includes('Points') || fieldKey.includes('Sum');
				const isQualified = fieldKey.includes('Qualified');

				let isDiff = false;
				if (isPointsField) {
					isDiff = Math.abs(Number(rowValue) - Number(systemValue ?? 0)) > 0.001;
				} else if (isQualified) {
					isDiff = Boolean(rowValue) !== Boolean(systemValue);
				} else {
					isDiff = normalizeText(rowValue as SimpleValue) !== normalizeText(systemValue);
				}

				diffs[fieldKey] = { isDiff, systemValue };
				if (isDiff) {
					hasDiff = true;
				}
			});

			if (hasDiff) {
				diffCount += 1;
			}

			verifiedRows.push({ row, status: 'matched', diffs, student });
		});

		const missingStudents = students.filter((s) => !usedStudentIds.has(s.id));
		missingStudents.forEach((student) => {
			report.push({
				status: 'error',
				message: `Brak w protokole: ${student.firstName} ${student.lastName} (${student.school?.city ?? '-'})`,
			});
		});

		report.unshift({
			status: 'success',
			message: `Podsumowanie: wierszy ${rows.length}, dopasowane ${matchedCount}, z różnicami ${diffCount}, nadmiar ${extraCount}, niejednoznaczne ${ambiguousCount}, brak w protokole ${missingStudents.length}.`,
		});

		setVerificationRows(verifiedRows);
		setVerificationReport(report);
		setVerificationLoading(false);
		saveMapping(stageKey, headerMapping, identityFields);
	};

	const handleClearVerification = () => {
		setVerificationText('');
		setVerificationRows([]);
		setVerificationReport([]);
		setActiveTab(0);
	};

	const handleResetMapping = () => {
		const auto: HeaderMapping = {};
		headers.forEach((header, index) => {
			const mapped = autoMapHeader(header);
			if (mapped) {
				auto[index] = mapped;
			}
		});
		setHeaderMapping(auto);
	};

	const handleClearMapping = () => {
		setHeaderMapping({});
		saveMapping(stageKey, {}, identityFields);
	};

	const toggleIdentityField = (fieldKey: string) => {
		setIdentityFields((prev) => (prev.includes(fieldKey) ? prev.filter((f) => f !== fieldKey) : [...prev, fieldKey]));
	};

	const getCellSx = (isDiff?: boolean, isStar?: boolean) => {
		if (isDiff) {
			return { backgroundColor: diffColor };
		}
		if (isStar) {
			return { backgroundColor: starColor };
		}
		return undefined;
	};

	const formatValue = (value: string | number | boolean | null | undefined): string => {
		if (value === null || value === undefined) return '-';
		if (typeof value === 'boolean') return value ? 'Tak' : 'Nie';
		return String(value);
	};

	// Get columns to display in the diff table based on mapping
	const displayColumns = useMemo(() => {
		const cols: { key: string; label: string }[] = [];
		// Always show index
		cols.push({ key: 'index', label: 'Lp.' });
		// Add identity fields first
		identityFields.forEach((fieldKey) => {
			const option = fieldOptions.find((f) => f.key === fieldKey);
			if (option && !cols.some((c) => c.key === fieldKey)) {
				cols.push({ key: fieldKey, label: option.label });
			}
		});
		// Add other mapped fields
		mappedFieldKeys.forEach((fieldKey) => {
			if (!cols.some((c) => c.key === fieldKey)) {
				const option = fieldOptions.find((f) => f.key === fieldKey);
				if (option) {
					cols.push({ key: fieldKey, label: option.label });
				}
			}
		});
		cols.push({ key: 'status', label: 'Status' });
		return cols;
	}, [identityFields, mappedFieldKeys]);

	const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
		setActiveTab(newValue);
	};

	const handleVerifyAndShowResults = () => {
		handleVerify();
		// Switch to report tab after verification
		setActiveTab(2);
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth={false}
		>
			<DialogTitle>{title}</DialogTitle>
			<Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
				<Tabs
					value={activeTab}
					onChange={handleTabChange}
					variant='scrollable'
					scrollButtons='auto'
				>
					<Tab label='Dane wejściowe' />
					<Tab
						label='Mapowanie'
						disabled={!headers.length}
					/>
					<Tab
						label={`Raport${verificationReport.length ? ` (${verificationReport.length})` : ''}`}
						disabled={!verificationReport.length}
					/>
					<Tab
						label={`Tabela${verificationRows.length ? ` (${verificationRows.length})` : ''}`}
						disabled={!verificationRows.length}
					/>
				</Tabs>
			</Box>
			<DialogContent sx={{ minHeight: 400 }}>
				{/* Tab 0: Dane wejściowe */}
				{activeTab === 0 && (
					<Box>
						<Typography
							variant='body2'
							sx={{ mb: 2 }}
						>
							Wklej tabelę z protokołu (tabulatory + nowe linie). Porównujemy z listą widoczną w tabeli po filtrach.
						</Typography>
						<TextField
							fullWidth
							multiline
							minRows={12}
							maxRows={20}
							value={verificationText}
							onChange={(event) => setVerificationText(event.target.value)}
							placeholder='Wklej protokół...'
						/>
						{headers.length > 0 && (
							<Typography
								variant='body2'
								color='success.main'
								sx={{ mt: 2 }}
							>
								Rozpoznano {headers.length} kolumn. Przejdź do zakładki "Mapowanie" aby skonfigurować pola.
							</Typography>
						)}
					</Box>
				)}

				{/* Tab 1: Mapowanie */}
				{activeTab === 1 && (
					<Box>
						<Typography variant='subtitle2'>Pola identyfikacyjne (muszą się zgadzać)</Typography>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1, mb: 3 }}>
							{identityFieldKeys
								.filter((key) => key !== 'index')
								.map((fieldKey) => {
									const option = fieldOptions.find((f) => f.key === fieldKey);
									return (
										<FormControlLabel
											key={fieldKey}
											control={
												<Checkbox
													size='small'
													checked={identityFields.includes(fieldKey)}
													onChange={() => toggleIdentityField(fieldKey)}
												/>
											}
											label={option?.label ?? fieldKey}
										/>
									);
								})}
						</Box>

						<Typography variant='subtitle2'>Mapowanie nagłówków</Typography>
						<Box
							sx={{
								mt: 1,
								display: 'grid',
								gridTemplateColumns: 'minmax(220px, 1fr) minmax(280px, 280px)',
								gap: 1,
								maxHeight: 750,
								overflowY: 'auto',
							}}
						>
							{headers.map((header, index) => (
								<Box
									key={`map-${index}`}
									sx={{ display: 'contents' }}
								>
									<Typography variant='body2'>{header || `Kolumna ${index + 1}`}</Typography>
									<FormControl size='small'>
										<InputLabel id={`header-select-${index}`}>Pole</InputLabel>
										<Select
											labelId={`header-select-${index}`}
											label='Pole'
											value={headerMapping[index] ?? 'ignore'}
											onChange={(event) =>
												setHeaderMapping((prev) => ({
													...prev,
													[index]: event.target.value,
												}))
											}
										>
											{mappingOptions.map((option) => (
												<MenuItem
													key={option.key}
													value={option.key}
												>
													{option.category === 'identity' ? '🔑 ' : option.category === 'result' ? '📊 ' : ''}
													{option.label}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Box>
							))}
						</Box>
						<Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
							<Button
								variant='outlined'
								size='small'
								onClick={() => saveMapping(stageKey, headerMapping, identityFields)}
							>
								Zapisz mapowanie
							</Button>
							<Button
								variant='outlined'
								size='small'
								onClick={handleResetMapping}
							>
								Auto mapowanie
							</Button>
							<Button
								variant='text'
								size='small'
								color='warning'
								onClick={handleClearMapping}
							>
								Wyczyść mapowanie
							</Button>
						</Box>
					</Box>
				)}

				{/* Tab 2: Raport */}
				{activeTab === 2 && (
					<Box>
						{verificationReport.length > 0 ? (
							<Box sx={{ maxHeight: 1000, overflowY: 'auto' }}>
								{verificationReport.map((item, index) => (
									<Typography
										key={`${item.status}-${index}`}
										color={item.status === 'success' ? 'success.main' : 'error.main'}
										variant='body2'
										sx={{ mb: 0.5 }}
									>
										{item.message}
									</Typography>
								))}
							</Box>
						) : (
							<Typography
								variant='body2'
								color='text.secondary'
							>
								Brak raportu. Wklej dane i kliknij "Weryfikuj".
							</Typography>
						)}
					</Box>
				)}

				{/* Tab 3: Tabela */}
				{activeTab === 3 && (
					<Box>
						{verificationRows.length > 0 ? (
							<TableContainer
								component={Paper}
								sx={{ maxHeight: 1000 }}
							>
								<Table
									size='small'
									stickyHeader
								>
									<TableHead>
										<TableRow>
											{displayColumns.map((col) => (
												<TableCell key={col.key}>{col.label}</TableCell>
											))}
										</TableRow>
									</TableHead>
									<TableBody>
										{verificationRows.map((item, rowIndex) => {
											const row = item.row || {};
											const diffs = item.diffs || {};
											const hasDiffs = Object.values(diffs).some((d) => d.isDiff);
											let statusLabel = 'OK';
											if (item.status === 'extra') {
												statusLabel = 'Brak w systemie';
											} else if (item.status === 'ambiguous') {
												statusLabel = 'Wiele dopasowań';
											} else if (hasDiffs) {
												statusLabel = 'Różnice';
											}

											return (
												<TableRow key={`row-${rowIndex}`}>
													{displayColumns.map((col) => {
														if (col.key === 'status') {
															return <TableCell key={col.key}>{statusLabel}</TableCell>;
														}
														const cellValue = row[col.key];
														const diffInfo = diffs[col.key];
														const hasStar = row.flags?.[col.key];
														const isDiff = diffInfo?.isDiff;
														const systemValue = diffInfo?.systemValue;

														const cellContent = formatValue(cellValue as SimpleValue);
														const cellSx = getCellSx(isDiff, hasStar);

														if (isDiff && systemValue !== undefined) {
															return (
																<Tooltip
																	key={col.key}
																	title={`W systemie: ${formatValue(systemValue)}`}
																	arrow
																>
																	<TableCell sx={cellSx}>{cellContent}</TableCell>
																</Tooltip>
															);
														}

														return (
															<TableCell
																key={col.key}
																sx={cellSx}
															>
																{cellContent}
															</TableCell>
														);
													})}
												</TableRow>
											);
										})}
									</TableBody>
								</Table>
							</TableContainer>
						) : (
							<Typography
								variant='body2'
								color='text.secondary'
							>
								Brak wyników. Wklej dane i kliknij "Weryfikuj".
							</Typography>
						)}
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button
					variant='outlined'
					onClick={handleClearVerification}
				>
					Wyczyść
				</Button>
				<Button onClick={onClose}>Zamknij</Button>
				<Button
					variant='contained'
					onClick={handleVerifyAndShowResults}
					disabled={verificationLoading || !verificationText.trim()}
				>
					{verificationLoading ? 'Weryfikuję...' : 'Weryfikuj'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ProtocolVerificationDialog;
